import { Controller } from "@hotwired/stimulus";

// The one parameter that opens THIS dialog. A plain dialog's `dialog_src` is not
// ours to remove — it may still be open above this one.
const POST_DIALOG_URL_PARAM = "post_dialog_src";

export default class extends Controller {
  static targets = ["modal"];

  connect() {
    this.modalTarget.addEventListener(
      "close",
      this.enableBodyScroll.bind(this)
    );

    this._mousedownHandler = this.handleMouseDown.bind(this);
    this._mouseupHandler = this.handleMouseUp.bind(this);
    this._handleAnchorScroll = this.handleAnchorScroll.bind(this);

    this.modalTarget.addEventListener("mousedown", this._mousedownHandler);
    this.modalTarget.addEventListener("mouseup", this._mouseupHandler);
    this.modalTarget.addEventListener("cancel", (event) => {
      if (this.modalTarget.dataset.closedBy === "none") {
        event.preventDefault();
        event.stopPropagation();

        requestAnimationFrame(() => {
          this.modalTarget.showModal();
        });
      }
    });

    if (this.modalTarget.dataset.postDialogSrc) {
      this.show();
    }

    // Handle anchor scrolling after turbo frame loads content
    this.modalTarget.addEventListener('turbo:frame-load', this._handleAnchorScroll)

  }

  disconnect() {
    this.modalTarget.removeEventListener(
      "close",
      this.enableBodyScroll.bind(this)
    );
    this.modalTarget.removeEventListener("mousedown", this._mousedownHandler);
    this.modalTarget.removeEventListener("mouseup", this._mouseupHandler);
    this.modalTarget.removeEventListener('turbo:frame-load', this._handleAnchorScroll);
  }

  show() {
    // Safari iOS 26 fix: Set both html and body background
    document.documentElement.style.backgroundColor = '#ffffff';
    document.body.style.backgroundColor = '#ffffff';
    this.modalTarget.showModal();
    this.disableBodyScroll();
  }

  close() {
    this.clearDialog();
    this.modalTarget.close();
  }

  clearDialog() {
    this.handleWindowLocationOnClose();

    const currentClass =
      this.modalTarget.children[0].getAttribute("class") ||
      "dialog__turbo-frame post__dialog-show";
    this.modalTarget.innerHTML = `<turbo-frame id='post_dialog_modal' class='${currentClass}'></turbo-frame>`;
  }

  // The comments path is rewritten as before — that part is what tells a closed
  // post dialog where the page underneath belongs. What changed is the query
  // string: only this dialog's own parameter is dropped. It used to take the
  // whole search with it, which removed unrelated page state (a selected range,
  // for instance) and the `dialog_src` of a plain dialog that may still be open.
  handleWindowLocationOnClose() {
    const regex = /\/posts\/\d+\/comments$/;
    const url = new URL(window.location.href);

    if (url.searchParams.has("ref") && url.searchParams.get("ref") === "trending") {
      url.pathname = url.pathname.replace(regex, "");
    } else {
      url.pathname = url.pathname.replace(regex, "/posts");
    }

    url.searchParams.delete(POST_DIALOG_URL_PARAM);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }

  handleMouseDown(event) {
    this.mousedownOutside = event.target === this.modalTarget;
  }

  handleMouseUp(event) {
    if (this.mousedownOutside && event.target === this.modalTarget) {
      this.close();
    }
    this.mousedownOutside = false;
  }

  disableBodyScroll() {
    // Save the current scroll position
    this.scrollY = window.scrollY;

    // Use overflow: hidden instead of position: fixed
    // position: fixed breaks touch text selection on iOS/Android
    document.body.style.overflow = "hidden";
  }

  enableBodyScroll() {
    const openDialogs = document.querySelectorAll("dialog[open]");
    if (openDialogs.length === 0) {
      document.body.style.removeProperty("overflow");
      // Safari iOS 26 fix
      document.documentElement.style.removeProperty("background-color");
      document.body.style.removeProperty("background-color");
    }

    // Restore the scroll position
    if (this.scrollY !== undefined) {
      window.scrollTo(0, this.scrollY);
      this.scrollY = undefined;
    }
  }

  clickOutside(event) {
    if (event.target === this.modalTarget && this.closedByValue !== "none") {
      this.close();
    }
  }

  submitEnd(event) {
    if (event.detail.success) {
      this.close();
    }
  }

  handleAnchorScroll(event) {
    // Only handle if it's the post_dialog_modal frame
    if (event.target.id !== 'post_dialog_modal') return

    // Check frame src URL for _anchor parameter
    const frameSrc = event.target.getAttribute('src')
    if (!frameSrc) return

    const frameUrl = new URL(frameSrc, window.location.origin)
    const anchorParam = frameUrl.searchParams.get('_anchor')

    if (!anchorParam) return

    // Wait for frame to fully render
    setTimeout(() => {
      const commentElement = this.modalTarget.querySelector(`#${anchorParam}`)

      if (commentElement) {
        // Scroll within the dialog
        commentElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })

        // Add temporary highlight (removed after 2.5 seconds)
        commentElement.classList.add('highlight-comment')
        setTimeout(() => {
          commentElement.classList.remove('highlight-comment')
        }, 2500)
      }
    }, 100) // Small delay for DOM to be ready
  }
}
